import { ManuallyTriggeredAudioHandler } from '@shared/models/manuallyTriggeredAudioHandler';
import { AudioContext } from '@shared/models/audioContext';
import { useCallback, useEffect, useRef } from 'react';
import useFeatures from '@shared/hooks/useFeatures';

interface Message {
  play: () => Promise<void>;
  cancellable: boolean;
  onPlayed?: () => void;
}

export default function useAudio(): AudioContext {
  const { manualAudio } = useFeatures();
  const isPlayingMessage = useRef<boolean>(false);
  const manualInformationHandlers = useRef<ManuallyTriggeredAudioHandler[]>([]);
  const voicedMessageQueue = useRef<Message[]>([]);

  const registerManualHandler = useCallback(
    (manualHandler: ManuallyTriggeredAudioHandler) => {
      manualInformationHandlers.current = [
        ...manualInformationHandlers.current,
        manualHandler,
      ].sort((a, b) => a.priority - b.priority);
    },
    [],
  );

  const unregisterManualHandler = useCallback(
    (manualHandler: ManuallyTriggeredAudioHandler) => {
      manualInformationHandlers.current =
        manualInformationHandlers.current.filter(
          (handler) => handler !== manualHandler,
        );
    },
    [],
  );

  const tryPlayNextMessage = useCallback(() => {
    const currentMessage = voicedMessageQueue.current[0];
    if (
      isPlayingMessage.current ||
      voicedMessageQueue.current.length === 0 ||
      !currentMessage
    ) {
      return false;
    }
    isPlayingMessage.current = true;
    void currentMessage.play().then(() => {
      voicedMessageQueue.current.shift();
      currentMessage.onPlayed?.();
      isPlayingMessage.current = false;
      tryPlayNextMessage();
    });
    return true;
  }, []);

  const playMessage = useCallback(
    (play: () => Promise<void>) => {
      return new Promise<void>((resolve) => {
        voicedMessageQueue.current.push({
          play,
          cancellable: false,
          onPlayed: resolve,
        });
        tryPlayNextMessage();
      });
    },
    [tryPlayNextMessage],
  );

  const tryCancelCurrentUtterance = useCallback(() => {
    if (
      voicedMessageQueue.current.length === 0 ||
      !voicedMessageQueue.current[0]
    ) {
      return false;
    }
    if (voicedMessageQueue.current[0].cancellable) {
      window.speechSynthesis.cancel();
    }
    return true;
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.code === 'KeyR') {
        if (tryCancelCurrentUtterance()) {
          return;
        }
        for (const manualHandler of manualInformationHandlers.current) {
          voicedMessageQueue.current.push({
            play: () => manualHandler.play(),
            cancellable: true,
          });
        }

        tryPlayNextMessage();
      }
    }

    if (!manualAudio) {
      return;
    }

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [
    manualAudio,
    manualInformationHandlers,
    tryCancelCurrentUtterance,
    tryPlayNextMessage,
  ]);

  return {
    registerManualHandler,
    unregisterManualHandler,
    playMessage,
  };
}
